import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { NumberStepperProps } from './forms.types';

const NumberStepper = React.forwardRef<HTMLDivElement, NumberStepperProps>(
  (
    {
      value,
      onChange,
      min = 0,
      max = Infinity,
      step = 1,
      unit,
      label,
      error,
      helpText,
      required = false,
      disabled = false,
      size = 'md',
      enableLongPress = true,
      fill = false,
      formatValue: formatValueProp,
      renderValue,
      'aria-label': ariaLabel,
      'data-testid': testId,
      className = '',
    },
    ref
  ) => {
    const reactId = React.useId();
    const stepperId = `stepper-${reactId}`;

    const [isDecrementing, setIsDecrementing] = useState(false);
    const [isIncrementing, setIsIncrementing] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Refs to avoid stale closures in long-press intervals
    const valueRef = useRef(value);
    const minRef = useRef(min);
    const maxRef = useRef(max);
    const stepRef = useRef(step);
    const disabledRef = useRef(disabled);

    useEffect(() => {
      valueRef.current = value;
    }, [value]);
    useEffect(() => {
      minRef.current = min;
    }, [min]);
    useEffect(() => {
      maxRef.current = max;
    }, [max]);
    useEffect(() => {
      stepRef.current = step;
    }, [step]);
    useEffect(() => {
      disabledRef.current = disabled;
    }, [disabled]);

    const canDecrement = value > min && !disabled;
    const canIncrement = value < max && !disabled;

    const handleDecrement = useCallback(() => {
      if (valueRef.current > minRef.current && !disabledRef.current) {
        const newValue = Math.max(
          minRef.current,
          valueRef.current - stepRef.current
        );
        onChange(Number(newValue.toFixed(10)));
      }
    }, [onChange]);

    const handleIncrement = useCallback(() => {
      if (valueRef.current < maxRef.current && !disabledRef.current) {
        const newValue = Math.min(
          maxRef.current,
          valueRef.current + stepRef.current
        );
        onChange(Number(newValue.toFixed(10)));
      }
    }, [onChange]);

    const startLongPress = useCallback(
      (action: 'decrement' | 'increment') => {
        if (!enableLongPress) return;

        const handler =
          action === 'decrement' ? handleDecrement : handleIncrement;

        timeoutRef.current = setTimeout(() => {
          intervalRef.current = setInterval(handler, 75);
        }, 400);
      },
      [enableLongPress, handleDecrement, handleIncrement]
    );

    const stopLongPress = useCallback(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setIsDecrementing(false);
      setIsIncrementing(false);
    }, []);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (disabled) return;

        switch (e.key) {
          case 'ArrowUp':
          case 'ArrowRight':
            e.preventDefault();
            handleIncrement();
            break;
          case 'ArrowDown':
          case 'ArrowLeft':
            e.preventDefault();
            handleDecrement();
            break;
          case 'Home':
            e.preventDefault();
            if (min !== -Infinity) {
              onChange(min);
            }
            break;
          case 'End':
            e.preventDefault();
            if (max !== Infinity) {
              onChange(max);
            }
            break;
        }
      },
      [disabled, handleIncrement, handleDecrement, min, max, onChange]
    );

    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, []);

    const sizeConfig = {
      sm: {
        button: 'w-10 h-10',
        icon: 'w-4 h-4',
        value: 'text-body-lg',
        unit: 'text-label-xs',
        gap: 'gap-2',
        minWidth: 'min-w-[100px]',
      },
      md: {
        button: 'w-11 h-11',
        icon: 'w-5 h-5',
        value: 'text-heading-sm font-medium',
        unit: 'text-label-sm',
        gap: 'gap-3',
        minWidth: 'min-w-[120px]',
      },
      lg: {
        button: 'w-12 h-12',
        icon: 'w-6 h-6',
        value: 'text-heading-md font-medium',
        unit: 'text-body-sm',
        gap: 'gap-4',
        minWidth: 'min-w-[140px]',
      },
    };

    const config = sizeConfig[size];

    const buttonBaseClasses = `
      ${config.button}
      flex items-center justify-center
      rounded-full
      border border-stroke-default
      bg-contrast-700
      text-neutral-300
      transition-all duration-fast
      focus:outline-none focus-visible:ring-state-focus
    `;

    const buttonEnabledClasses = `
      hover:bg-contrast-600 hover:border-stroke-strong hover:text-neutral-50
      active:bg-contrast-700 active:scale-95
    `;

    const buttonDisabledClasses = `
      opacity-40 cursor-not-allowed
    `;

    const formatValue = (val: number): string => {
      if (formatValueProp) return formatValueProp(val);
      if (Number.isInteger(step)) {
        return val.toString();
      }
      const decimals = step.toString().split('.')[1]?.length || 0;
      return val.toFixed(decimals);
    };
    const formatted = formatValue(value);

    const message = error || helpText;
    const messageId = `${stepperId}-message`;
    const labelId = `${stepperId}-label`;

    return (
      <div ref={ref} className={`space-y-1.5 ${className}`}>
        {label && (
          <span
            id={labelId}
            className={`block text-body-sm font-medium ${
              error ? 'text-error-400' : 'text-neutral-300'
            }`}
          >
            {label}
            {required && <span className="text-primary-400 ml-1">*</span>}
          </span>
        )}

        <div
          id={stepperId}
          role="spinbutton"
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max === Infinity ? undefined : max}
          aria-valuetext={
            formatValueProp || renderValue ? formatted : undefined
          }
          aria-labelledby={label ? labelId : undefined}
          aria-label={label ? undefined : (ariaLabel ?? 'Number stepper')}
          aria-describedby={message ? messageId : undefined}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={handleKeyDown}
          data-testid={testId}
          className={`${fill ? 'flex w-full justify-between' : 'inline-flex'} items-center ${config.gap} outline-none focus-visible:ring-state-focus rounded-lg`}
        >
          {/* Decrement button */}
          <button
            type="button"
            onClick={handleDecrement}
            onMouseDown={() => {
              setIsDecrementing(true);
              startLongPress('decrement');
            }}
            onMouseUp={stopLongPress}
            onMouseLeave={stopLongPress}
            onTouchStart={() => {
              setIsDecrementing(true);
              startLongPress('decrement');
            }}
            onTouchEnd={stopLongPress}
            disabled={!canDecrement}
            aria-label="Decrease value"
            className={`
              ${buttonBaseClasses}
              ${canDecrement ? buttonEnabledClasses : buttonDisabledClasses}
              ${isDecrementing && canDecrement ? 'scale-95 bg-contrast-600' : ''}
            `}
          >
            <MinusIcon className={config.icon} />
          </button>

          {/* Value display */}
          {renderValue ? (
            <div
              className={`min-w-0 flex-1 select-none ${disabled ? 'opacity-40' : ''}`}
            >
              {renderValue(formatted)}
            </div>
          ) : (
            <div
              className={`
              ${config.minWidth}
              flex items-baseline justify-center
              select-none
              ${disabled ? 'opacity-40' : ''}
            `}
            >
              <span
                className={`
                ${config.value}
                font-mono
                text-neutral-50
                tabular-nums
                tracking-tight
              `}
              >
                {formatted}
              </span>
              {unit && (
                <span
                  className={`
                  ${config.unit}
                  text-neutral-500
                  ml-1.5
                  uppercase
                  tracking-wide
                `}
                >
                  {unit}
                </span>
              )}
            </div>
          )}

          {/* Increment button */}
          <button
            type="button"
            onClick={handleIncrement}
            onMouseDown={() => {
              setIsIncrementing(true);
              startLongPress('increment');
            }}
            onMouseUp={stopLongPress}
            onMouseLeave={stopLongPress}
            onTouchStart={() => {
              setIsIncrementing(true);
              startLongPress('increment');
            }}
            onTouchEnd={stopLongPress}
            disabled={!canIncrement}
            aria-label="Increase value"
            className={`
              ${buttonBaseClasses}
              ${canIncrement ? buttonEnabledClasses : buttonDisabledClasses}
              ${isIncrementing && canIncrement ? 'scale-95 bg-contrast-600' : ''}
            `}
          >
            <PlusIcon className={config.icon} />
          </button>
        </div>

        {message && (
          <p
            id={messageId}
            className={`text-label-xs ${error ? 'text-error-400' : 'text-neutral-500'}`}
          >
            {message}
          </p>
        )}
      </div>
    );
  }
);

NumberStepper.displayName = 'NumberStepper';

export default NumberStepper;
